import {
  parseUtmParams,
  readAttributionQuery,
  registerPostHogAttribution,
} from "@filmustage/shared"

// posthog-js silently DROPS a capture() made while opted out, and the first events of a
// visit - `landing_visit`, the first `$pageview` - fire on short timers that the Defender
// handshake routinely loses. Measured 2026-10-06: `landing_visit` reached PostHog in about
// half of the sessions, and 27% of sessions with a CTA click had no pageview at all. So an
// event waits here for the opt-in instead: sent late, never lost.
let releaseCapture
const captureAllowed = new Promise(resolve => {
  releaseCapture = resolve
})

/**
 * Let the queued events through, and every later one straight away. Called by
 * gatsby-browser.js right after it opts PostHog in.
 *
 * @returns {void}
 */
export const allowCapture = () => releaseCapture()

const captureWhenAllowed = send => captureAllowed.then(send)

/**
 * Fire a landing-side PostHog event with the UTM cookie expanded into both
 * top-level utm_* properties and a Segment-shaped `context.campaign` object.
 * The campaign shape is retained so downstream destinations that previously
 * mapped Segment context.campaign keep their wiring without remapping.
 *
 * `posthog` is set on `window` in gatsby-browser.js#onClientEntry. Capture is
 * opted out until the Defender handshake completes, so events go only over the
 * unblocked proxy; an event fired before that is queued (see `allowCapture`),
 * and this function still returns without waiting for it.
 *
 * @param {string} action - Event name (e.g. "landing_visit", "landing_sign_up_click").
 * @param {Record<string, unknown>} [add] - Extra properties for the event.
 */
export const analyticsAction = async (action, add) => {
  // Acquisition (utm_*/click ids) plus the internal CTA (fm_page/fm_cta) in
  // one string - the CTA keys ride along as their own properties and never
  // reach `campaign.source`/`campaign.medium`.
  const utmCookieValue = readAttributionQuery()
  const utmParams = parseUtmParams(utmCookieValue)

  const campaignParams = {
    source: utmParams.utm_source || "",
    medium: utmParams.utm_medium || "",
    name: utmParams.utm_campaign || "",
    content: utmParams.utm_content || "",
    term: utmParams.utm_term || "",
    ...Object.fromEntries(
      Object.entries(utmParams).filter(([key]) => !key.startsWith("utm_")),
    ),
  }

  const baseProps = {
    ...utmParams,
    campaign: campaignParams,
    // Mirror Segment's `context.campaign` so existing destination mappings
    // that read `$context.campaign.*` keep working.
    $context: { campaign: campaignParams },
  }

  if (typeof window === "undefined" || !window.posthog) return

  // Refresh attribution super-properties on every event so a `li_fat_id`
  // cookie that LinkedIn Insight Tag set after the initial onClientEntry
  // boot still lands as a person property in time for the next capture.
  // Idempotent: register/register_once are no-ops when values match.
  registerPostHogAttribution(window.posthog, utmCookieValue, {
    referrer: typeof document !== "undefined" ? document.referrer || "" : "",
    url: typeof window !== "undefined" ? window.location.href || "" : "",
  })

  if (action === "user_load_page") {
    captureWhenAllowed(() => window.posthog.capture("$pageview", baseProps))
    return
  }

  setTimeout(() => {
    captureWhenAllowed(() =>
      window.posthog.capture(action, { ...baseProps, ...add }),
    )
  }, 200)
}
